/**
 * DRPX Courier — Business Constants & Config Functions
 *
 * Zentrale Geschäfts-Konstanten (Abo-Preise, Fahrzeugtypen, Ausstattung,
 * Zertifikate, Status-Farben, Impressum). Preise sind NETTO gespeichert,
 * Brutto = Netto × 1.19. Werte werden zur Laufzeit durch
 * `initPlattformSettings()` mit DB-Daten überschrieben.
 *
 * @module config/constants
 */

import { safeGetLS, safeSetLS, safeParseJSON } from '../lib/storage.js';
import type {
  TranslateFn,
  PlattformSettings,
  CustomFahrzeug,
  CustomAusstattung,
  CustomZertifikat,
} from '../types.js';

// ═══════════════════════════════════════════════════════════════
// Business Constants — Preise sind NETTO, Brutto = Netto * 1.19
// ═══════════════════════════════════════════════════════════════

/** Aktueller Mehrwertsteuersatz (19%). */
export const MWST_SATZ = 0.19;
export let PROBE_MONATE = 3;
/** Abo Netto-Preise (B2B). Werden durch initPlattformSettings() zur Laufzeit überschrieben. */
export let FIRMA_ABO_MONAT = 79.99;
/** @see FIRMA_ABO_MONAT */
export let FIRMA_ABO_JAHR = 799.99;
/** @see FIRMA_ABO_MONAT */
export let FAHRER_ABO_MONAT = 49.99;
/** @see FIRMA_ABO_MONAT */
export let FAHRER_ABO_JAHR = 499.99;
/** DRPX-Pro (bis zu 5 Zugänge), netto. Rückfallwert = Entscheidung Sertan 17.10.2026. @see FIRMA_ABO_MONAT */
export let PRO_ABO_MONAT = 59;
/** @see PRO_ABO_MONAT */
export let PRO_ABO_JAHR = 599;

// ═══════════════════════════════════════════════════════════════
// Katalog-Overrides: disabled/custom Fahrzeuge, Ausstattung, Zertifikate
// ═══════════════════════════════════════════════════════════════

let _disabledFahrzeuge: string[] = [];
let _customFahrzeuge: CustomFahrzeug[] = [];
let _disabledAusstattung: string[] = [];
let _customAusstattung: CustomAusstattung[] = [];
let _disabledZertifikate: string[] = [];
let _customZertifikate: CustomZertifikat[] = [];

/** Wendet Abo-Preise und Katalog-Overrides an. */
function _applySettings(ps: PlattformSettings): void {
  if (!ps) return; // Defensive: JS-Caller können null/undefined übergeben
  if (ps.probeMonate) PROBE_MONATE = ps.probeMonate;
  if (ps.firmaAboMonat) FIRMA_ABO_MONAT = ps.firmaAboMonat;
  if (ps.firmaAboJahr) FIRMA_ABO_JAHR = ps.firmaAboJahr;
  if (ps.fahrerAboMonat) FAHRER_ABO_MONAT = ps.fahrerAboMonat;
  if (ps.fahrerAboJahr) FAHRER_ABO_JAHR = ps.fahrerAboJahr;
  if (ps.proAboMonat) PRO_ABO_MONAT = ps.proAboMonat;
  if (ps.proAboJahr) PRO_ABO_JAHR = ps.proAboJahr;
  _disabledFahrzeuge = ps.disabledFahrzeuge ?? [];
  _customFahrzeuge = ps.customFahrzeuge ?? [];
  _disabledAusstattung = ps.disabledAusstattung ?? [];
  _customAusstattung = ps.customAusstattung ?? [];
  _disabledZertifikate = ps.disabledZertifikate ?? [];
  _customZertifikate = ps.customZertifikate ?? [];
}

// ═══════════════════════════════════════════════════════════════
// Landing-Page Statistiken & Impressum
// ═══════════════════════════════════════════════════════════════

/** Landing-Page Statistiken (überschreibbar via Admin-Settings). */
export let LP_STAT_AUFTRAEGE = '1.200+';
/** @see LP_STAT_AUFTRAEGE */
export let LP_STAT_UNTERNEHMEN = '340+';
/** @see LP_STAT_AUFTRAEGE */
export let LP_STAT_PROVISION = '0%';
/** @see LP_STAT_AUFTRAEGE */
export let LP_STAT_SPRACHEN = '7';
/** Sichtbarkeits-Flags für Landing-Page-Statistiken. */
export const LP_STAT_SHOW = { auftraege: false, unternehmen: false, provision: true, sprachen: true };

/** Impressum-Daten (Fallback-Defaults — werden aus DB durch _applyAllSettings überschrieben). */
export let FIRMA_NAME = 'DRPX Courier';
/** @see FIRMA_NAME */
export let FIRMA_GF = '';
/** @see FIRMA_NAME */
export let FIRMA_STRASSE = 'Erbesgasse 6';
/** @see FIRMA_NAME */
export let FIRMA_PLZ_ORT = '61203 Reichelsheim (Wetterau)';
/** @see FIRMA_NAME */
export let FIRMA_EMAIL = 'info@drpxcourier.de';
/** @see FIRMA_NAME */
export let FIRMA_TELEFON = '';
/** @see FIRMA_NAME */
export let FIRMA_UST_ID = '';
/** @see FIRMA_NAME */
export let FIRMA_HRB = '';
/** @see FIRMA_NAME */
export let FIRMA_VERANTWORTLICH = '';

/** Wendet alle Settings an (Abo + Landing-Stats + Impressum). */
function _applyAllSettings(ps: PlattformSettings): void {
  if (!ps) return; // Defensive: JS-Caller können null/undefined übergeben
  _applySettings(ps);
  // Landing-Stats
  if (ps.statAuftraege) LP_STAT_AUFTRAEGE = ps.statAuftraege;
  if (ps.statUnternehmen) LP_STAT_UNTERNEHMEN = ps.statUnternehmen;
  if (ps.statProvision) LP_STAT_PROVISION = ps.statProvision;
  if (ps.statSprachen) LP_STAT_SPRACHEN = ps.statSprachen;
  if (ps.statShowAuftraege !== undefined) LP_STAT_SHOW.auftraege = !!ps.statShowAuftraege;
  if (ps.statShowUnternehmen !== undefined) LP_STAT_SHOW.unternehmen = !!ps.statShowUnternehmen;
  if (ps.statShowProvision !== undefined) LP_STAT_SHOW.provision = !!ps.statShowProvision;
  if (ps.statShowSprachen !== undefined) LP_STAT_SHOW.sprachen = !!ps.statShowSprachen;
  // Impressum
  if (ps.firmaName) FIRMA_NAME = ps.firmaName;
  if (ps.firmaGF) FIRMA_GF = ps.firmaGF;
  if (ps.firmaStrasse) FIRMA_STRASSE = ps.firmaStrasse;
  if (ps.firmaPlzOrt) FIRMA_PLZ_ORT = ps.firmaPlzOrt;
  if (ps.firmaEmail) FIRMA_EMAIL = ps.firmaEmail;
  if (ps.firmaTelefon) FIRMA_TELEFON = ps.firmaTelefon;
  if (ps.firmaUstId) FIRMA_UST_ID = ps.firmaUstId;
  if (ps.firmaHrb) FIRMA_HRB = ps.firmaHrb;
  if (ps.firmaVerantwortlich) FIRMA_VERANTWORTLICH = ps.firmaVerantwortlich;
}

// Einmaliges Parse beim Modulimport
try {
  _applyAllSettings(safeParseJSON(safeGetLS('drpx_plattform_settings'), {}) as PlattformSettings);
} catch {
  /* ignored */
}

// Stand der Einstellungen: zählt bei jedem initPlattformSettings hoch, damit
// Seiten, die Werte nur beim ersten Anzeigen lesen (Startseite), neu zeichnen.
let _settingsStand = 0;
const _settingsHoerer = new Set<() => void>();

/** Aktueller Stand (für `useSyncExternalStore`). */
export function plattformSettingsStand(): number {
  return _settingsStand;
}

/**
 * Bei jeder Änderung der Plattform-Einstellungen benachrichtigen lassen.
 *
 * @returns Funktion zum Abmelden
 */
export function abonnierePlattformSettings(cb: () => void): () => void {
  _settingsHoerer.add(cb);
  return () => {
    _settingsHoerer.delete(cb);
  };
}

/**
 * Überschreibt alle Business-Constants mit Werten aus Supabase plattform_settings
 * und merkt sie sich im Browser (`drpx_plattform_settings`) — dort lesen
 * Rechtstexte (`getLegalSections`), Startseiten-Texte (`lpText`), „Über uns"
 * und Mail-Texte.
 *
 * Aufrufer: beim Öffnen der App auch OHNE Anmeldung (`ladePlattformSettings`,
 * 18.10.2026), nach dem Login (`useAuth.loadData`) und nach dem Speichern im
 * Admin. Vorher wurde der Browser-Speicher nur beim Speichern im Admin
 * geschrieben: Neue Besucher und Nutzer auf anderen Geräten sahen
 * Standard-Preise, Standard-AGB und Standard-Texte.
 */
export function initPlattformSettings(settingsObj: PlattformSettings): void {
  _applyAllSettings(settingsObj);
  safeSetLS('drpx_plattform_settings', JSON.stringify(settingsObj || {}));
  _settingsStand++;
  _settingsHoerer.forEach((cb) => cb());
}

// ═══════════════════════════════════════════════════════════════
// Fahrzeug-Labels & Kategorien
// ═══════════════════════════════════════════════════════════════

/**
 * Gibt die Fahrzeugtyp-Labels zurück (braucht t für i18n).
 * Berücksichtigt deaktivierte/custom Fahrzeugtypen aus Admin-Settings.
 */
export function getFahrzeugLabels(t: TranslateFn): Record<string, string> {
  const labels: Record<string, string> = {
    pkw: t('vPkw'),
    caddy: t('vCaddy'),
    transporter_klein: t('vTransKlein'),
    transporter: t('vTransporter'),
    transporter_lang: t('vSprinter'),
    koffer_35: t('vKoffer35'),
    lkw_75: t('vLkw75'),
    lkw_12: t('vLkw12'),
    lkw_18: t('vLkw18'),
    lkw_40: t('vLkw40'),
    planenauflieger: t('vPlane'),
    kuehlkoffer: t('vKuehl'),
    tankfahrzeug: t('vTank'),
    silo: t('vSilo'),
    tieflader: t('vTieflader'),
    containerchassis: t('vContainer'),
    wechselbruecke: t('vWechsel'),
    autotransporter: t('vAuto'),
    haenger: t('vAnhaenger'),
    fahrradkurier: t('vFahrrad'),
    motorrad: t('vMotorrad'),
    egal: t('vEgal'),
  };
  for (const k of _disabledFahrzeuge) {
    delete labels[k];
  }
  for (const c of _customFahrzeuge) {
    labels[c.key] = c.label;
  }
  return labels;
}

/** Fahrzeug-Kategorie mit zugeordneten Schlüsseln. */
interface FahrzeugKategorie {
  id: string;
  label: string;
  keys: string[];
}

/** Gibt Fahrzeug-Kategorien gruppiert zurück (Klein, Transporter, LKW, Spezial). */
export function getFahrzeugKategorien(t: TranslateFn): FahrzeugKategorie[] {
  const kats: FahrzeugKategorie[] = [
    { id: 'klein', label: t('vKatKlein'), keys: ['fahrradkurier', 'motorrad', 'pkw', 'caddy'] },
    {
      id: 'transporter',
      label: t('vKatTransporter'),
      keys: ['transporter_klein', 'transporter', 'transporter_lang', 'koffer_35'],
    },
    { id: 'lkw', label: t('vKatLkw'), keys: ['lkw_75', 'lkw_12', 'lkw_18', 'lkw_40'] },
    {
      id: 'spezial',
      label: t('vKatSpezial'),
      keys: [
        'planenauflieger',
        'kuehlkoffer',
        'tankfahrzeug',
        'silo',
        'tieflader',
        'containerchassis',
        'wechselbruecke',
        'autotransporter',
        'haenger',
      ],
    },
    { id: 'sonstig', label: t('hcStoppSonstiges'), keys: ['egal'] },
  ];
  for (const kat of kats) {
    kat.keys = kat.keys.filter((key) => !_disabledFahrzeuge.includes(key));
  }
  for (const c of _customFahrzeuge) {
    const kat = kats.find((k) => k.id === c.kategorie) ?? kats[kats.length - 1];
    kat.keys.push(c.key);
  }
  return kats.filter((k) => k.keys.length > 0);
}

// ═══════════════════════════════════════════════════════════════
// Ausstattungs-Optionen
// ═══════════════════════════════════════════════════════════════

/** Einzelne Ausstattungsoption (Hebebühne, GPS, etc.). */
interface AusstattungOption {
  key: string;
  label: string;
  icon: string;
  gruppe: string;
}

/** Gibt alle Ausstattungsoptionen zurück. Berücksichtigt Admin-Overrides. */
export function getAusstattungOptionen(t: TranslateFn): AusstattungOption[] {
  let opts: AusstattungOption[] = [
    { key: 'ladebordwand', label: t('eqLadebordwand'), icon: 'upload', gruppe: 'lade' },
    { key: 'hubwagen', label: t('eqHubwagen'), icon: 'wrench', gruppe: 'lade' },
    { key: 'stapler', label: t('eqStapler'), icon: 'crane', gruppe: 'lade' },
    { key: 'zweitePerson', label: t('eqBeifahrer'), icon: 'users', gruppe: 'lade' },
    { key: 'spanngurte', label: t('eqSpanngurte'), icon: 'link', gruppe: 'lade' },
    { key: 'antirutschmatten', label: t('eqAntirutsch'), icon: 'pallet', gruppe: 'lade' },
    { key: 'decken', label: t('eqDecken'), icon: 'couch', gruppe: 'lade' },
    { key: 'gdpKonform', label: t('eqGDP'), icon: 'thermometer', gruppe: 'spezial' },
    { key: 'kuehlfahrzeug', label: t('eqKuehl'), icon: 'snowflake', gruppe: 'spezial' },
    { key: 'tiefkuehl', label: t('eqTiefkuehl'), icon: 'ice-cube', gruppe: 'spezial' },
    { key: 'temperaturschreiber', label: t('eqTempSchreiber'), icon: 'chart', gruppe: 'spezial' },
    { key: 'isoCert', label: t('eqISO'), icon: 'clipboard', gruppe: 'spezial' },
    { key: 'adr', label: t('eqADR'), icon: 'biohazard', gruppe: 'spezial' },
    { key: 'lebensmittel', label: t('eqLebensmittel'), icon: 'apple', gruppe: 'spezial' },
    { key: 'haccp', label: t('eqHACCP'), icon: 'check-circle', gruppe: 'spezial' },
    // Compliance/Recht-Anforderungen (eingefügt 23.05.2026):
    // Auftraggeber kann diese als Pflicht-Anforderung setzen; Fahrer
    // ohne passendes Zertifikat sehen beim Bieten eine Warnung
    // (Logik in zertifikat-pflicht.ts via pflichtFuer-Mapping).
    { key: 'milog', label: t('eqMiLoG'), icon: 'shield', gruppe: 'spezial' },
    { key: 'eu_lizenz', label: t('eqEULizenz'), icon: 'globe', gruppe: 'spezial' },
    { key: 'express', label: t('eqExpress'), icon: 'lightning', gruppe: 'service' },
    { key: 'overnight', label: t('eqOvernight'), icon: 'moon', gruppe: 'service' },
    { key: 'direktfahrt', label: t('eqDirektfahrt'), icon: 'target', gruppe: 'service' },
    { key: 'termintreu', label: t('eqZeitfenster'), icon: 'clock', gruppe: 'service' },
    { key: 'wochenende', label: t('eqWochenende'), icon: 'calendar', gruppe: 'service' },
    { key: 'gps', label: t('eqGPS'), icon: 'satellite', gruppe: 'tracking' },
    { key: 'fotodoku', label: t('eqFoto'), icon: 'camera', gruppe: 'tracking' },
    { key: 'scannerPOD', label: t('eqScanner'), icon: 'mobile', gruppe: 'tracking' },
    { key: 'warenversicherung', label: t('eqWarenvers'), icon: 'shield', gruppe: 'versicherung' },
    { key: 'hochwertig', label: t('eqHochwert'), icon: 'diamond', gruppe: 'versicherung' },
    { key: 'zollabwicklung', label: t('eqZoll'), icon: 'customs', gruppe: 'versicherung' },
    { key: 'sicherheitsschuhe', label: t('eqSchuhe'), icon: 'shoe', gruppe: 'psa' },
    { key: 'warnweste', label: t('eqWarnweste'), icon: 'safety-vest', gruppe: 'psa' },
    { key: 'helm', label: t('eqHelm'), icon: 'helmet', gruppe: 'psa' },
  ];
  opts = opts.filter((o) => !_disabledAusstattung.includes(o.key));
  for (const c of _customAusstattung) {
    opts.push({ key: c.key, label: c.label, icon: c.icon ?? 'wrench', gruppe: c.gruppe ?? 'lade' });
  }
  return opts;
}

/** Gibt die Ausstattungs-Gruppenbezeichnungen zurück (Lade, Spezial, Temperatur, etc.). */
export function getAusstattungGruppen(t: TranslateFn): Record<string, string> {
  return {
    lade: t('grpLade'),
    spezial: t('grpSpezial'),
    service: t('grpService'),
    tracking: t('grpTracking'),
    versicherung: t('grpVers'),
    psa: t('grpPSA'),
  };
}

// ═══════════════════════════════════════════════════════════════
// Status-Farben
// ═══════════════════════════════════════════════════════════════

/** Farb-Map für Marktplatz-Auftragsstatus (offen, vergeben, unterwegs, etc.). */
export const STATUS_COLORS: Record<string, string> = {
  offen: '#f59e0b',
  angebote: '#f59e0b',
  vergeben: '#16a34a',
  beladen: '#16a34a',
  unterwegs: '#16a34a',
  angekommen: '#16a34a',
  geliefert: '#16a34a',
  abgeschlossen: '#64748b',
  abgerechnet: '#64748b',
  storniert: '#ef4444',
  abgelaufen: '#94a3b8',
  bezahlt: '#64748b',
  erledigt: '#f59e0b',
};

/**
 * i18n-Schlüssel je Auftragsstatus.
 *
 * Einzige Quelle für die Beschriftung eines Status. Wer einen Status
 * anzeigt, schlägt hier nach — NIE `t(status)` direkt aufrufen: Die
 * Statuswerte sind Datenbank-Werte, keine i18n-Schlüssel, und ein
 * fehlender Schlüssel fällt bei `t()` stumm auf den Schlüsselnamen
 * zurück (siehe `i18n/index.tsx`). Genau so stand bis 15.09.2026 das
 * deutsche Wort „angekommen" in allen sieben Sprachen im Tourenplaner.
 */
export const STATUS_I18N: Record<string, string> = {
  offen: 'statusOffen',
  angebote: 'statusAngebote',
  vergeben: 'statusVergeben',
  beladen: 'statusBeladen',
  unterwegs: 'statusUnterwegs',
  angekommen: 'statusAngekommen',
  geliefert: 'statusGeliefert',
  abgeschlossen: 'statusAbgeschlossen',
  abgerechnet: 'statusAbgerechnet',
  storniert: 'statusStorniert',
  abgelaufen: 'statusAbgelaufen',
  bezahlt: 'statusBezahlt',
  erledigt: 'statusErledigt',
};

/** Farb-Map für Rechnungsstatus (offen, versendet, bezahlt, storniert). */
export const RE_STATUS_COLORS: Record<string, string> = {
  offen: '#f59e0b',
  versendet: '#16a34a',
  bezahlt: '#64748b',
  storniert: '#ef4444',
};

// ═══════════════════════════════════════════════════════════════
// Zertifikate
// ═══════════════════════════════════════════════════════════════

/** Konfiguration eines Zertifikatstyps. */
interface ZertifikatConfig {
  key: string;
  label: string;
  icon: string;
  beschreibung: string;
  pflichtFuer: string[];
  farbe: string;
}

/** Gibt alle verfügbaren Zertifikate zurück (ADR, Kühlnachweis, etc.) mit Pflichtfeld-Zuordnung. */
export function getZertifikate(t: TranslateFn): ZertifikatConfig[] {
  let zerts: ZertifikatConfig[] = [
    {
      key: 'adr_basis',
      label: t('zAdrBasis'),
      icon: 'biohazard',
      beschreibung: t('zAdrBasisDesc'),
      pflichtFuer: ['adr'],
      farbe: '#16a34a',
    },
    {
      key: 'gdp_zertifikat',
      label: t('zGDP'),
      icon: 'thermometer',
      beschreibung: t('zGDPDesc'),
      pflichtFuer: ['gdpKonform'],
      farbe: '#16a34a',
    },
    {
      key: 'haccp_schulung',
      label: t('zHACCP'),
      icon: 'check-circle',
      beschreibung: t('zHACCPDesc'),
      pflichtFuer: ['haccp', 'lebensmittel'],
      farbe: '#16a34a',
    },
    {
      key: 'lebensmittel_hygiene',
      label: t('zIfSG'),
      icon: 'apple',
      beschreibung: t('zIfSGDesc'),
      pflichtFuer: ['lebensmittel', 'haccp'],
      farbe: '#16a34a',
    },
    {
      key: 'kuehltransport',
      label: t('zKuehl'),
      icon: 'snowflake',
      beschreibung: t('zKuehlDesc'),
      pflichtFuer: ['kuehlfahrzeug', 'tiefkuehl', 'temperaturschreiber'],
      farbe: '#16a34a',
    },
    {
      key: 'berufskraftfahrer',
      label: t('zBKF'),
      icon: 'id-card',
      beschreibung: t('zBKFDesc'),
      pflichtFuer: [],
      farbe: '#16a34a',
    },
    {
      key: 'stapler_schein',
      label: t('zStapler'),
      icon: 'crane',
      beschreibung: t('zStaplerDesc'),
      pflichtFuer: ['stapler'],
      farbe: '#16a34a',
    },
    {
      key: 'zoll_zugelassen',
      label: t('zAEO'),
      icon: 'customs',
      beschreibung: t('zAEODesc'),
      pflichtFuer: ['zollabwicklung'],
      farbe: '#16a34a',
    },
    {
      key: 'versicherung_transport',
      label: t('zTranspVers'),
      icon: 'shield',
      beschreibung: t('zTranspVersDesc'),
      pflichtFuer: ['warenversicherung', 'hochwertig'],
      farbe: '#16a34a',
    },
    {
      key: 'versicherung_erweitert',
      label: t('zErwVers'),
      icon: 'diamond',
      beschreibung: t('zErwVersDesc'),
      pflichtFuer: ['hochwertig'],
      farbe: '#16a34a',
    },
    // Compliance-Zertifikate (eingefügt 23.05.2026):
    // MiLoG und EU-Lizenz sind keine klassischen Schulungszertifikate,
    // sondern Nachweis-Dokumente. Aus Sicht des Zertifikat-Systems
    // verhalten sie sich aber gleich: Upload + Admin-Review + Ablauf.
    {
      key: 'milog_erklaerung',
      label: t('zMiLoG'),
      icon: 'shield',
      beschreibung: t('zMiLoGDesc'),
      pflichtFuer: ['milog'],
      farbe: '#16a34a',
    },
    {
      key: 'eu_lizenz',
      label: t('zEULizenz'),
      icon: 'globe',
      beschreibung: t('zEULizenzDesc'),
      pflichtFuer: ['eu_lizenz'],
      farbe: '#16a34a',
    },
  ];
  zerts = zerts.filter((z) => !_disabledZertifikate.includes(z.key));
  for (const c of _customZertifikate) {
    zerts.push({
      key: c.key,
      label: c.label,
      icon: c.icon ?? 'scroll',
      beschreibung: c.beschreibung ?? '',
      pflichtFuer: c.pflichtFuer ?? [],
      farbe: c.farbe ?? '#16a34a',
    });
  }
  return zerts;
}

/** Farb-Map für Zertifikat-Status (gueltig, pruefung, abgelaufen, fehlt). */
export const ZERTIFIKAT_STATUS_COLORS: Record<string, string> = {
  gueltig: '#16a34a',
  pruefung: '#f59e0b',
  abgelaufen: '#ef4444',
  fehlt: '#94a3b8',
};

/** Ermittelt welche Zertifikate für eine gegebene Ausstattung Pflicht sind. */
export function benoetigteZertifikate(ausstattung: Record<string, boolean> | null, t: TranslateFn): string[] {
  if (!ausstattung) return [];
  const needed: string[] = [];
  for (const z of getZertifikate(t)) {
    const isRequired = z.pflichtFuer.some((key) => ausstattung[key]);
    if (isRequired && !needed.includes(z.key)) needed.push(z.key);
  }
  return needed;
}

// ═══════════════════════════════════════════════════════════════
// Transport-Anweisungen Vorlage
// ═══════════════════════════════════════════════════════════════

/** Standard-Vorlagetext für Transport-Anweisungen (ADSp, Frachtbriefe, Ladungssicherung, MiLoG). */
export const TRANSPORT_ANWEISUNGEN_VORLAGE =
  'Wir arbeiten ausschlie\u00DFlich auf Grund der allgem. Deutschen Spediteurbedingungen (ADSp) neueste Fassung. Soweit Bef\u00F6rderungsleistungen erbracht werden, gelten die AGNB-KVO-CMR.\n\nFrachtrechnungen werden nur unter gleichzeitiger Vorlage des originalbest\u00E4tigten Frachtbriefes bzw. Lieferscheines unter Angabe von Datum, Uhrzeit, Unterschrift und Namensgeber in Klarschrift zur Zahlung f\u00E4llig.\n\nDie R\u00FCckf\u00FChrung der Lieferscheine innerhalb von 3 Tagen gilt als vereinbart. Sofern diese Frist \u00FCberschritten ist bzw. bei unvollst\u00E4ndigen Frachtpapieren gilt eine Frachtk\u00FCrzung von 5%.\n\nEs gilt Beiladungs- & Umladeverbot. Alle auftretenden Probleme sind umgehend zu melden. Absoluter Kundenschutz sowie Neutralit\u00E4t gilt als vereinbart.\n\nLadungssicherung und das Tragen von Sicherheitsschuhen ist Pflicht. Nichteinhaltung f\u00FChrt zur Stornierung des Auftrages.\n\nWartezeiten und sonstige Zusatzkosten werden nur nach schriftlicher Zustimmung verg\u00FCtet. Ansonsten sind alle Anspr\u00FCche mit der vereinbarten Fracht abgegolten.\n\nMindestlohn:\nMit der Annahme unseres Auftrages verpflichtet sich der Subunternehmer, alle ihm aufgrund des Mindestlohngesetzes (MiLoG) obliegenden Pflichten einzuhalten.';

/**
 * Standard-Zustellart je Benachrichtigungs-Kategorie.
 *
 * EINZIGE Quelle für diese Werte. Am 02.09.2026 aus `useNotifications.ts`
 * hierher verschoben, nachdem Migration 118 versehentlich einen zweiten Satz
 * Standardwerte in der Datenbank angelegt hatte — mit 'app' statt 'both' für
 * `neuesZertifikat`. Der Admin bekam dadurch die Glocke, aber keine Mail.
 *
 * Seither gibt der Aufrufer den Standardwert an die Datenbankfunktion mit
 * (Migration 119); geraten wird dort nichts mehr.
 *
 * Seit 20.09.2026 liest auch die Einstellungsseite (`NotificationSettings`)
 * nur noch von hier. Vorher hatte sie eine eigene Liste, die bei
 * `angebotAbgelehnt` 'app' anzeigte, während tatsächlich 'both' galt.
 * `aboErinnerung` ist entfernt (Schalter seit 05.09.2026 weg, Dienstmitteilung).
 */
export const NOTIF_DEFAULTS: Record<string, string> = {
  neuerAuftrag: 'app',
  // Freie Rückfahrten (20.09.2026, Migration 123/124): Der Fahrer hat die
  // Strecke selbst gemerkt und will es wissen — deshalb Glocke UND Mail.
  rueckfahrtTreffer: 'both',
  neuesAngebot: 'app',
  angebotAngenommen: 'both',
  angebotAbgelehnt: 'both',
  statusAenderung: 'app',
  zertFreigegeben: 'app',
  neuesZertifikat: 'both',
  kycEingereicht: 'both',
  neueBewertung: 'app',
  neuerNutzer: 'both',
  neuerAuftragAdmin: 'app',
  chatNachricht: 'app',
  // Werbung, muss abbestellbar bleiben (§ 7 UWG). Wird NICHT über
  // `pushNotif` verschickt, sondern von der Cron-Function
  // `check-inactive-subscribers`, die als Edge Function diese Datei nicht
  // importieren kann und denselben Wert selbst kennt
  // (`prefs.reEngagement || 'email'`). Hier nur, damit die Einstellungsseite
  // ihn anzeigt — bei Änderung BEIDE Stellen anpassen.
  reEngagement: 'email',
};

